import { useRef, type KeyboardEvent, type MouseEvent, type PointerEvent, type ReactNode, type Ref } from "react";

/** Ascending or descending, the two directions any sortable column supports. */
export type SortDir = "asc" | "desc";

/** The table's current sort, or undefined for no active sort. `key` matches a column's `sortKey`. */
export interface SortState {
  key: string;
  dir: SortDir;
}

export interface Column<Row> {
  key: string;
  header: string;
  cell: (row: Row) => ReactNode;
  /** Right-aligned, tabular numbers. */
  numeric?: boolean;
  /** Allow this cell to wrap inside a compact table (Thai summaries). */
  wrap?: boolean;
  /**
   * Makes this column's header a click-to-sort button; the value passed to `onSort` and compared against `sort.key`
   * (e.g. the contract's `WorkItemSortKey`). Omit for a column with no sort affordance (e.g. free-text Summary).
   */
  sortKey?: string;
  /** Batch 16: the width a column takes when the table is sized (some column was resized) and this one was not. */
  defaultWidth?: number;
}

interface DataTableProps<Row> {
  caption: string;
  columns: readonly Column<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row) => string;
  /** Scroll inside the wrapper with a sticky header and first column (PMO Console .tablewrap.tall). */
  tall?: boolean;
  /** One line per row (PMO Console table.compact). */
  compact?: boolean;
  emptyText?: string;
  /** Extra class per row, e.g. a selected row or a breached SLA bar. */
  rowClassName?: (row: Row) => string | undefined;
  /** Current sort, matched against each column's `sortKey`. Omit (with `onSort`) for a plain, unsortable table. */
  sort?: SortState;
  /** Called with a column's `sortKey` when its header is activated (click or keyboard). The caller owns the toggle
   * logic (ascending first, flip on a repeat click of the same column) and persists the result whatever way it likes. */
  onSort?: (key: string) => void;
  /** The scrolling wrapper, for a caller that remembers its position (GRF-16: ItemTable, so Back returns to the row). */
  wrapperRef?: Ref<HTMLDivElement>;
  /**
   * Batch 16 (Items list column chooser): column widths in pixels by column key. With `onResize` every header gets a resize
   * handle: drag its edge, or focus it and press Left/Right (a `separator` with its width as the value), or double-click it to
   * fit the content (`onResize(key, <measured width>)`; Enter, or a browser that cannot measure, sends `onResize(key, null)`, the
   * default width). Without `onResize` the table is unchanged.
   */
  widths?: Readonly<Record<string, number | undefined>>;
  onResize?: (key: string, width: number | null) => void;
  /** The accessible name of a column's resize handle. */
  resizeLabel?: (header: string) => string;
  /** Smallest and largest width the handle allows, and the step of one arrow press. */
  resizeLimits?: { min: number; max: number; step: number };
  /** Dims the rows while newer ones load (spec §6.2: the table never flashes empty). */
  busy?: boolean;
  /**
   * Batch 19 GRF-60: a click anywhere on a row (the caller decides which clicks count, e.g. not on a link or a button). The row
   * is not a tab stop; keyboard users reach the same action through a link in the row.
   */
  onRowClick?: (row: Row, event: MouseEvent<HTMLTableRowElement>) => void;
}

export function DataTable<Row>({
  caption,
  columns,
  rows,
  rowKey,
  tall = false,
  compact = true,
  emptyText = "No items",
  rowClassName,
  sort,
  onSort,
  wrapperRef,
  widths,
  onResize,
  resizeLabel = (header) => `Resize ${header}`,
  resizeLimits = { min: 60, max: 600, step: 16 },
  busy = false,
  onRowClick,
}: DataTableProps<Row>) {
  const resizable = onResize !== undefined;
  // D-B16-01: true from the end of a resize drag until the events of that release are done, so the click a browser may still
  // send to a header (the pointer let go over it) never sorts. Cleared on the next task, after that click.
  const justResized = useRef(false);
  const sized = resizable && widths !== undefined && Object.values(widths).some((width) => width !== undefined);
  // Fixed layout once a column is sized: every column gets a width (its own, else its default), and the table is the sum (at least
  // the wrapper's width) and scrolls sideways, so one wide column never squeezes the others to slivers.
  const widthOf = (column: Column<Row>) => widths?.[column.key] ?? column.defaultWidth ?? 140;
  const tableWidth = sized ? columns.reduce((sum, column) => sum + widthOf(column), 0) : undefined;
  return (
    // A scrollable region must be keyboard-reachable and named (WCAG 2.1.1, 1.4.10).
    <div ref={wrapperRef} className={tall ? "tablewrap tall" : "tablewrap"} role="region" aria-label={caption} tabIndex={0} aria-busy={busy || undefined}>
      <table
        className={[compact ? "compact" : "", sized ? "gf-sized" : "", busy ? "gf-busy" : ""].filter(Boolean).join(" ") || undefined}
        style={tableWidth !== undefined ? { width: tableWidth, minWidth: "100%" } : undefined}
      >
        <caption className="vh">{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => {
              // Batch 31 GRF-110: a header is a sort button only when the table can sort (`onSort`); otherwise it is plain text
              // with no `aria-sort`, never a button that does nothing.
              const sortKey = onSort ? column.sortKey : undefined;
              return (
                <th
                  key={column.key}
                  scope="col"
                  className={[column.numeric ? "num" : "", resizable ? "gf-resizable" : ""].filter(Boolean).join(" ") || undefined}
                  aria-sort={sortKey ? ariaSortOf(sort, sortKey) : undefined}
                  // The resize handle is named ("Resize Key"); without this the header's own name would read "Key Resize Key".
                  aria-label={resizable ? column.header : undefined}
                  style={sized ? { width: widthOf(column) } : undefined}
                >
                  {sortKey ? (
                    <button
                      type="button"
                      className="gf-sortbtn"
                      onClick={() => {
                        if (justResized.current) return;
                        onSort?.(sortKey);
                      }}
                    >
                      {column.header}
                      <SortGlyph dir={sort?.key === sortKey ? sort.dir : undefined} />
                    </button>
                  ) : (
                    column.header
                  )}
                  {resizable && (
                    <ResizeHandle
                      label={resizeLabel(column.header)}
                      width={widths?.[column.key]}
                      limits={resizeLimits}
                      onResize={(width) => onResize?.(column.key, width)}
                      onDragEnd={() => {
                        justResized.current = true;
                        window.setTimeout(() => {
                          justResized.current = false;
                        }, 0);
                      }}
                    />
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length}>{emptyText}</td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={rowKey(row)} className={rowClassName?.(row)} onClick={onRowClick ? (event) => onRowClick(row, event) : undefined}>
                {columns.map((column) => (
                  <td key={column.key} className={cellClass(column)}>
                    {column.cell(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function ariaSortOf(sort: SortState | undefined, columnKey: string): "ascending" | "descending" | "none" {
  if (sort?.key !== columnKey) return "none";
  return sort.dir === "asc" ? "ascending" : "descending";
}

/** 10px up/down carets; the active direction is solid, the other dim — a visible cue alongside `aria-sort`, never the only one. */
function SortGlyph({ dir }: { dir: SortDir | undefined }) {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" focusable="false" className="gf-sortglyph">
      <path d="M5 1l3 3.2H2z" fill={dir === "asc" ? "currentColor" : "var(--ink-45)"} />
      <path d="M5 9L2 5.8h6z" fill={dir === "desc" ? "currentColor" : "var(--ink-45)"} />
    </svg>
  );
}

function cellClass<Row>(column: Column<Row>): string | undefined {
  const classes = [column.numeric ? "num" : "", column.wrap ? "wrapok" : ""].filter(Boolean);
  return classes.length > 0 ? classes.join(" ") : undefined;
}

/**
 * The width that shows a column's longest value on one line: its header label and every rendered cell, measured as laid out
 * (a wrapping cell is measured unwrapped), plus the cell's own padding. Null where the browser cannot measure (no layout).
 */
function fitColumnWidth(th: HTMLTableCellElement): number | null {
  const table = th.closest("table");
  if (!table || typeof document.createRange !== "function") return null;
  const range = document.createRange();
  if (typeof range.getBoundingClientRect !== "function") return null;
  const index = th.cellIndex;
  const contentWidth = (node: Node) => {
    range.selectNodeContents(node);
    return range.getBoundingClientRect().width;
  };
  const padding = (element: Element) => {
    const style = getComputedStyle(element);
    return (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
  };
  // The header: the sort button's label (its own padding), or the header's text; never the handle itself.
  const button = th.querySelector<HTMLElement>(".gf-sortbtn");
  let widest = button ? contentWidth(button) + padding(button) : 0;
  if (!button) {
    for (const child of Array.from(th.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) widest = Math.max(widest, contentWidth(child) + padding(th));
    }
  }
  for (const row of Array.from(table.tBodies[0]?.rows ?? [])) {
    const cell = row.cells[index];
    if (!cell || cell.colSpan > 1) continue;
    const wrap = cell.style.whiteSpace;
    cell.style.whiteSpace = "nowrap";
    widest = Math.max(widest, contentWidth(cell) + padding(cell));
    cell.style.whiteSpace = wrap;
  }
  return widest > 0 ? Math.ceil(widest) + 2 : null;
}

/**
 * A column edge: drag it, or focus it and press Left/Right (one step each), Home/End for the limits; double-click fits the
 * content (Enter returns the column to its default width). A `separator` with the width as its value, so a screen reader hears
 * the change (spec §11). It sits inside its own header cell (D-B16-01): a handle hanging over the border was painted under the
 * next sticky header, so a mouse press landed on that header's sort button instead.
 */
function ResizeHandle({
  label,
  width,
  limits,
  onResize,
  onDragEnd,
}: {
  label: string;
  width: number | undefined;
  limits: { min: number; max: number; step: number };
  onResize: (width: number | null) => void;
  onDragEnd: () => void;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const clamp = (value: number) => Math.max(limits.min, Math.min(limits.max, Math.round(value)));
  const current = () => width ?? ref.current?.parentElement?.getBoundingClientRect().width ?? limits.min;

  function onPointerDown(event: PointerEvent<HTMLSpanElement>) {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startWidth = current();
    const handle = event.currentTarget;
    handle.setPointerCapture?.(event.pointerId);
    let moved = false;
    const move = (moveEvent: globalThis.PointerEvent) => {
      if (!moved && Math.abs(moveEvent.clientX - startX) < 2) return;
      moved = true;
      onResize(clamp(startWidth + moveEvent.clientX - startX));
    };
    const up = () => {
      handle.removeEventListener("pointermove", move);
      handle.removeEventListener("pointerup", up);
      handle.removeEventListener("pointercancel", up);
      if (moved) onDragEnd();
    };
    handle.addEventListener("pointermove", move);
    handle.addEventListener("pointerup", up);
    handle.addEventListener("pointercancel", up);
  }

  function fit() {
    const th = ref.current?.closest("th");
    const natural = th ? fitColumnWidth(th) : null;
    onResize(natural === null ? null : clamp(natural));
  }

  function onKeyDown(event: KeyboardEvent<HTMLSpanElement>) {
    const at = current();
    const next =
      event.key === "ArrowLeft" ? at - limits.step
      : event.key === "ArrowRight" ? at + limits.step
      : event.key === "Home" ? limits.min
      : event.key === "End" ? limits.max
      : null;
    if (next !== null) {
      event.preventDefault();
      onResize(clamp(next));
    } else if (event.key === "Enter") {
      event.preventDefault();
      onResize(null);
    }
  }

  return (
    <span
      ref={ref}
      className="gf-colresize"
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuemin={limits.min}
      aria-valuemax={limits.max}
      aria-valuenow={width !== undefined ? Math.round(width) : undefined}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      // A press on the handle is never a click on the header (it would sort).
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={(event) => {
        event.stopPropagation();
        fit();
      }}
    />
  );
}
